import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdsStep } from './AdsStep.tsx';
import { CabinetsStep } from './CabinetsStep.tsx';
import { LaunchSettingsStep } from './LaunchSettingsStep.tsx';
import { useLaunchWizard } from './useLaunchWizard.ts';

export function LaunchWizard() {
  const wizard = useLaunchWizard();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [launching, setLaunching] = useState(false);

  const next = () => {
    if (step === 1 && !wizard.templateId) return setError('Select a template');
    if (step === 2 && !Object.keys(wizard.targets).length) return setError('Select at least one account');
    setError('');
    setStep(step + 1);
  };

  const launch = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const result = await wizard.submitBatch();
      wizard.reset();
      navigate('/launch-queue', { state: { message: `Batch created: ${result.launches_count} launches queued` } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create batch');
      setLaunching(false);
    }
  };

  return (
    <div className="page-body">
      <div className="templates-head">
        <div>
          <h1>Launch wizard</h1>
          <p className="text-muted text-sm">Prepare campaigns, ad sets, and ads.</p>
        </div>
      </div>

      <div className="wizard-steps">
        {['Settings', 'Accounts', 'Ads'].map((label, i) => (
          <button
            type="button"
            key={label}
            onClick={() => setStep(i + 1)}
            className={step === i + 1 ? 'wizard-stepper wizard-stepper--active' : 'wizard-stepper'}
          >
            <b>{i + 1}</b>{label}
          </button>
        ))}
      </div>

      {error && <div className="notification notification--error">{error}</div>}

      {step === 1 && (
        <LaunchSettingsStep
          templates={wizard.templates}
          templateId={wizard.templateId}
          setTemplateId={wizard.setTemplateId}
          pauseSeconds={wizard.pauseSeconds}
          setPauseSeconds={wizard.setPauseSeconds}
          scheduledAt={wizard.scheduledAt}
          setScheduledAt={wizard.setScheduledAt}
        />
      )}

      {step === 2 && (
        <CabinetsStep
          accounts={wizard.adAccounts}
          pages={wizard.pages}
          targets={wizard.targets}
          pixels={wizard.pixels}
          payers={wizard.payers}
          selectedOrder={wizard.selectedOrder}
          templateBudget={wizard.templateBudget}
          onSelect={(id, yes) => wizard.selectAccount(id, yes, wizard.templateBudget, wizard.scheduledAt)}
          onUpdate={wizard.updateTarget}
          onBulkSelect={(ids) => wizard.bulkSelectAccounts(ids, wizard.templateBudget, wizard.scheduledAt)}
          loadPixels={wizard.loadPixels}
          loadPayers={wizard.loadPayers}
        />
      )}

      {step === 3 && (
        <AdsStep
          targets={wizard.targets}
          accounts={wizard.adAccounts}
          pages={wizard.pages}
          posts={wizard.posts}
          loadPosts={wizard.loadPosts}
          updateTarget={wizard.updateTarget}
          adsetsCount={wizard.adsetsCount}
          setAdsetsCount={wizard.setAdsetsCount}
          adsPerAdset={wizard.adsPerAdset}
          setAdsPerAdset={wizard.setAdsPerAdset}
          selectedOrder={wizard.selectedOrder}
          scheduledAt={wizard.scheduledAt}
        />
      )}

      <div className="template-form__actions">
        {step > 1 && (
          <button className="btn btn--secondary" onClick={() => setStep(step - 1)} disabled={launching}>
            Back
          </button>
        )}
        {step < 3 ? (
          <button className="btn btn--primary" onClick={next}>Next</button>
        ) : (
          <button className="btn btn--primary" onClick={launch} disabled={launching}>
            {launching ? 'Creating...' : 'Launch'}
          </button>
        )}
      </div>
    </div>
  );
}
